// src/migration/organizedImport.js
//
// Importación de reclamos desde el export "Claims-*.xlsx" de Organized
// Adjusting (OASync.jsx). Solo arma el plan — no escribe nada:
//   - Reclamos que ya están en la app (cruce por Claim Number normalizado;
//     el "Id" del export es un número de fila, no sirve): se completan SOLO
//     los campos vacíos, nunca se pisa lo cargado a mano.
//   - Reclamos que no están: se crean.
//   - El Status de Organized se traduce a un paso del flujo (settled y
//     archive/closed = Negociado, decisión del cliente 2026-09-24). "active"
//     y vacío quedan sin paso: se ubican después con la Migración.
//   - PA Assigned trae varios PA separados por coma (casi siempre Luis
//     Claudio + otro): el principal (comisión) es el primero que no es
//     supervisor de la empresa; los demás van a collaboratorAdjusters.
// El archivo se procesa en el navegador — nunca se sube ni entra al repo (PII).

export const cleanStr = value => (value === null || value === undefined ? '' : String(value).trim());

// El "*" en el Client Name de Organized marca los reclamos inscritos en el
// servicio de CSR (decisión del propietario 2026-09-29). Sin "*" el reclamo
// no forma parte del sistema. Ver NEXT_CSR_ENROLLMENT_ASTERISK.md.
export const hasCsrMark = value => cleanStr(value).includes('*');

// Organized marca algunos nombres con "*" y dobles espacios.
const cleanName = value => cleanStr(value).replace(/\*/g, '').replace(/\s+/g, ' ').trim();

// Tolera espacios, guiones, mayúsculas y ceros a la izquierda (Organized
// guarda "01009564503" y la app lo tenía como "1009564503").
export const normalizeClaimNumber = value => cleanStr(value).toUpperCase().replace(/[^A-Z0-9]/g, '').replace(/^0+(?=.)/, '');

// MM/DD/YY (o MM/DD/YYYY) → YYYY-MM-DD, el formato de los input type="date".
export const convertDate = value => {
  const match = /^(\d{1,2})\/(\d{1,2})\/(\d{2}|\d{4})$/.exec(cleanStr(value));
  if (!match) return '';
  const [, mm, dd, yy] = match;
  const yyyy = yy.length === 4 ? yy : String(2000 + parseInt(yy, 10));
  return `${yyyy}-${mm.padStart(2, '0')}-${dd.padStart(2, '0')}`;
};

const toNumber = value => {
  const n = Number(cleanStr(value).replace(/[$,%\s]/g, ''));
  return cleanStr(value) && Number.isFinite(n) ? n : null;
};

const splitName = full => {
  const name = cleanName(full);
  if (!name) return null;
  const cut = name.lastIndexOf(' ');
  return cut > 0 ? { firstName: name.slice(0, cut), lastName: name.slice(cut + 1), phone: '', email: '' } : { firstName: name, lastName: '', phone: '', email: '' };
};

// "123 Main St, Miami, FL 33101" → partes de la dirección de la pérdida.
const splitAddress = address => {
  const match = /^(.+),\s*([^,]+),\s*([A-Za-z]{2})\s*(\d{5}(?:-\d{4})?)?$/.exec(cleanStr(address));
  if (!match) return null;
  return { street: match[1].trim(), city: match[2].trim(), state: match[3].toUpperCase(), zip: match[4] || '' };
};

export const OA_STATUS_TO_STEP = {
  settled: 'closed',
  archive: 'closed',
  closed: 'closed',
  attorney: 'attorney_noi',
  'pre-litigation': 'attorney_noi',
  appraisal: 'appraisal',
  mediation: 'mediation',
  'scheduling mediation': 'mediation',
  negotiation: 'negotiation',
  'under review': 'awaiting_coverage',
  prospect: 'prospect'
};

export const stepForStatus = status => OA_STATUS_TO_STEP[cleanStr(status).toLowerCase()] || null;

// Una letra de diferencia (sustitución, falta o sobra): "Lara" ↔ "Jara".
const oneEditApart = (a, b) => {
  if (a === b) return true;
  if (Math.abs(a.length - b.length) > 1) return false;
  let i = 0;
  while (i < a.length && a[i] === b[i]) i += 1;
  return a.slice(i + 1) === b.slice(i + 1) || a.slice(i) === b.slice(i + 1) || a.slice(i + 1) === b.slice(i);
};

// Nombre de Organized → nombre del perfil en la app: exacto; o mismo primer
// y último nombre ("Luis R Claudio" ↔ "Luis Claudio"); o mismo primer nombre
// y el apellido con una letra de diferencia ("Julio Lara" en Organized ↔
// "Julio Jara" en producción), solo si hay un único perfil así.
export const matchAdjuster = (name, adjusters) => {
  const wanted = cleanName(name).toLowerCase();
  if (!wanted) return null;
  const exact = adjusters.find(a => cleanName(a.name).toLowerCase() === wanted);
  if (exact) return exact;
  const parts = wanted.split(' ');
  const first = parts[0];
  const last = parts[parts.length - 1];
  const tokens = a => cleanName(a.name).toLowerCase().split(' ');
  const sameEnds = adjusters.find(a => { const t = tokens(a); return t[0] === first && t[t.length - 1] === last; });
  if (sameEnds) return sameEnds;
  const close = adjusters.filter(a => { const t = tokens(a); return t[0] === first && parts.length > 1 && t.length > 1 && oneEditApart(t[t.length - 1], last); });
  return close.length === 1 ? close[0] : null;
};

export const assignPAs = (raw, adjusters) => {
  const names = cleanStr(raw).split(',').map(cleanName).filter(Boolean);
  const resolved = names.map(name => ({ raw: name, profile: matchAdjuster(name, adjusters) }));
  const unknown = resolved.filter(r => !r.profile).map(r => r.raw);
  const finalName = r => (r.profile ? r.profile.name : r.raw);
  // Primero un PA que existe en la app y no es supervisor; si no hay, uno desconocido.
  const principal = resolved.find(r => r.profile && !r.profile.viewAllClaims)
    || resolved.find(r => !r.profile) || resolved[0];
  const nonSupervisors = resolved.filter(r => !r.profile?.viewAllClaims);
  return {
    adjusterName: principal ? finalName(principal) : '',
    collaboratorAdjusters: resolved.filter(r => r !== principal).map(finalName),
    unknown,
    // Dos PA que no son supervisores: el principal se eligió por orden, conviene revisarlo.
    principalUnsure: nonSupervisors.length > 1
  };
};

// Una fila del export → campos del claimRecord (solo los que traen valor).
export const mapOrganizedRow = (row, adjusters = []) => {
  const pas = assignPAs(row['PA Assigned'], adjusters);
  const property = cleanStr(row['Property Address']);
  const mailing = cleanStr(row['Mailing Address']);
  const loss = splitAddress(property);
  const owner2 = splitName(row['Client Name 2']);
  const fields = {
    claimNumber: cleanStr(row['Claim Number']),
    clientName: cleanName(row['Client Name']),
    clientEmail: cleanStr(row.Email),
    clientPhone: cleanStr(row.Phone) || cleanStr(row['Mobile Number']),
    clientMobile: cleanStr(row['Mobile Number']),
    clientAddress: property,
    lossAddressStreet: loss?.street || '',
    lossAddressCity: loss?.city || '',
    lossAddressState: loss?.state || '',
    lossAddressZip: loss?.zip || '',
    mailingAddressStreet: mailing && mailing !== property ? mailing : '',
    policyNumber: cleanStr(row['Policy Number']),
    cause: cleanStr(row['Cause Of Loss']),
    incidentDate: convertDate(row['Date Of Loss']),
    dateCalledIn: convertDate(row['Date Called In']),
    contractSignedDate: convertDate(row['Contract Signed']),
    deductibleAmount: toNumber(row.Deductible),
    coverageA: toNumber(row['Coverage A']),
    coverageB: toNumber(row['Coverage B']),
    coverageC: toNumber(row['Coverage C']),
    coverageD: toNumber(row['Coverage D']),
    agreedFeePercent: toNumber(row['Adjuster Claim %']) ?? toNumber(row['Fee Schedule']),
    adjusterName: pas.adjusterName,
    collaboratorAdjusters: pas.collaboratorAdjusters,
    attorneyName: cleanName(row.Attorney),
    vendor: cleanName(row.Vendor),
    insuranceCompanyName: cleanStr(row['Insurance Company']),
    insuranceAdjusterName: cleanName(row['Insurance Adjuster']),
    insuranceAdjusterEmail: cleanStr(row['Insurance Adjuster Email']),
    insuranceAdjusterPhone: cleanStr(row['Insurance Adjuster Phone']) || cleanStr(row['Insurance Adjuster Mobile Number']),
    mortgageLoanNumber: cleanStr(row['Loan Number']),
    // Estado de Organized tal cual: alimenta la lista editable de estados con color.
    oaStatus: cleanStr(row.Status || row['File Type'])
  };
  if (mailing && mailing !== property) fields.mailingSameAsLoss = false;
  if (owner2) fields.owner2 = owner2;
  const present = Object.fromEntries(Object.entries(fields).filter(([, v]) => (Array.isArray(v) ? v.length > 0 : v !== '' && v !== null)));
  return {
    fields: present,
    status: cleanStr(row.Status || row['File Type']),
    stepId: stepForStatus(row.Status || row['File Type']),
    csrEnrolled: hasCsrMark(row['Client Name']),
    unknownPAs: pas.unknown,
    principalUnsure: pas.principalUnsure
  };
};

const isEmpty = value => value === undefined || value === null || value === ''
  || (Array.isArray(value) && value.length === 0)
  || (typeof value === 'object' && !Array.isArray(value) && Object.values(value).every(v => !cleanStr(v)));

// Llaves del segundo cruce (reclamos de la app sin Claim # o con otro):
// nombre sin acentos, signos ni orden ("Doe, Jane" = "Jane Doe") y
// dirección reducida a número de casa + zip.
const nameKey = value => cleanName(value).normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase()
  .replace(/[^a-z ]/g, ' ').split(' ').filter(Boolean).sort().join(' ');
const addressKey = value => {
  const text = cleanStr(value);
  const house = /^\s*(\d+)/.exec(text)?.[1];
  const zip = /(\d{5})(?:-\d{4})?\s*$/.exec(text)?.[1];
  return house && zip ? `${house}-${zip}` : '';
};
const claimAddressKey = cr => addressKey(cr.clientAddress)
  || addressKey([cr.lossAddressStreet, cr.lossAddressZip].filter(Boolean).join(' '));

// Los PA los manda Organized (decisión del cliente 2026-09-24): si trae otro
// PA principal, el reclamo cambia de principal (y con él la comisión, ver
// applyOrganizedImport: cheques y deudas de operador) y el anterior queda
// como otro PA asignado. Solo con un principal que existe como perfil.
const fillFor = (existing, mapped, knownNames = []) => {
  const diffs = {};
  Object.entries(mapped.fields).forEach(([field, value]) => {
    if (field === 'adjusterName' || field === 'collaboratorAdjusters') return;
    if (isEmpty(existing[field])) diffs[field] = value;
  });
  const current = cleanStr(existing.adjusterName);
  const incoming = cleanStr(mapped.fields.adjusterName);
  const lower = name => cleanStr(name).toLowerCase();
  let principalChange = null;
  if (incoming && lower(incoming) !== lower(current) && knownNames.includes(lower(incoming))) {
    diffs.adjusterName = incoming;
    principalChange = { from: current, to: incoming };
  }
  const principal = diffs.adjusterName || current;
  const others = [...new Set([...(existing.collaboratorAdjusters || []), current, incoming, ...(mapped.fields.collaboratorAdjusters || [])]
    .filter(name => name && lower(name) !== lower(principal)))];
  if (others.length > 0 && JSON.stringify(others) !== JSON.stringify(existing.collaboratorAdjusters || [])) diffs.collaboratorAdjusters = others;
  const stepId = !existing.flow?.stepId && mapped.stepId ? mapped.stepId : null;
  return { diffs, stepId, principalChange };
};

// Plan completo: qué se crea, qué se completa en reclamos existentes y qué
// hay que revisar. Dos cruces: 1) Claim # normalizado; 2) para lo que no
// cruzó, mismo cliente + misma fecha de pérdida o misma dirección. Si solo
// coincide el nombre, no se crea: queda como "posible duplicado" para
// revisar a mano (nunca se duplica un reclamo que ya está en producción).
export const buildOrganizedPlan = (rows, claimRecords, adjusters = []) => {
  const byNumber = new Map();
  claimRecords.forEach(cr => {
    const key = normalizeClaimNumber(cr.claimNumber);
    if (key) byNumber.set(key, [...(byNumber.get(key) || []), cr]);
  });
  const rowCount = new Map();
  rows.forEach(row => {
    const key = normalizeClaimNumber(row['Claim Number']);
    if (key) rowCount.set(key, (rowCount.get(key) || 0) + 1);
  });

  const plan = { create: [], fill: [], unchanged: [], duplicates: [], possibleDuplicates: [], unknownPAs: new Map(), unsurePrincipal: [], stepCounts: {}, matches: [], unmatched: [] };
  const used = new Set();
  const pending = [];
  const knownNames = adjusters.map(a => cleanStr(a.name).toLowerCase());
  const addMatch = (existing, mapped, matchedBy) => {
    used.add(existing.id);
    // Qué reclamo de la app corresponde a qué fila, y si esa fila lleva "*"
    // (inscrito en CSR): lo usa CsrEnrollment.jsx.
    plan.matches.push({ claimRecord: existing, csrEnrolled: mapped.csrEnrolled, matchedBy });
    const { diffs, stepId, principalChange } = fillFor(existing, mapped, knownNames);
    if (Object.keys(diffs).length > 0 || stepId) plan.fill.push({ claimRecord: existing, diffs, stepId, status: mapped.status, matchedBy, principalChange });
    else plan.unchanged.push(existing);
  };

  // 1) Claim #
  rows.forEach(row => {
    const mapped = mapOrganizedRow(row, adjusters);
    if (!mapped.fields.clientName) return;
    const key = normalizeClaimNumber(row['Claim Number']);
    mapped.unknownPAs.forEach(name => plan.unknownPAs.set(name, (plan.unknownPAs.get(name) || 0) + 1));
    const label = mapped.stepId || 'none';
    plan.stepCounts[label] = (plan.stepCounts[label] || 0) + 1;
    if (mapped.principalUnsure) plan.unsurePrincipal.push(mapped);

    // Mismo Claim Number repetido en el Excel o en la app: no se adivina.
    if (key && (rowCount.get(key) > 1 || (byNumber.get(key) || []).length > 1)) { plan.duplicates.push(mapped); return; }
    const existing = key ? byNumber.get(key)?.[0] : null;
    if (existing) addMatch(existing, mapped, 'claimNumber');
    else pending.push(mapped);
  });

  // 2) Cliente + fecha de pérdida o dirección, entre los reclamos de la app que no cruzaron.
  pending.forEach(mapped => {
    const wanted = nameKey(mapped.fields.clientName);
    const candidates = claimRecords.filter(cr => !used.has(cr.id) && nameKey(cr.clientName) === wanted);
    if (candidates.length === 0) { plan.create.push(mapped); return; }
    const sameDate = cr => mapped.fields.incidentDate && cleanStr(cr.incidentDate) === mapped.fields.incidentDate;
    const sameAddress = cr => { const k = addressKey(mapped.fields.clientAddress); return k && claimAddressKey(cr) === k; };
    // El Claim # de la app, si tiene, tiene que estar vacío: si es otro, son reclamos distintos del mismo cliente.
    const strong = candidates.filter(cr => (sameDate(cr) || sameAddress(cr)) && !normalizeClaimNumber(cr.claimNumber));
    if (strong.length === 1) addMatch(strong[0], mapped, sameDate(strong[0]) ? 'nameDate' : 'nameAddress');
    else plan.possibleDuplicates.push({ mapped, candidates });
  });

  // Reclamos de la app que ninguna fila del Excel identificó con seguridad
  // (Claim # repetido, sin cruce o posible duplicado): no se sabe si llevan "*".
  plan.unmatched = claimRecords.filter(cr => !used.has(cr.id));
  plan.unknownPAs = [...plan.unknownPAs.entries()].map(([name, count]) => ({ name, count }));
  return plan;
};
